/**
 * SuperAdmin Platform PnL service.
 *
 * Wraps GET /analytics/platform-pnl?date=YYYY-MM-DD — a SuperAdmin-only
 * cross-user snapshot consumed by SuperAdminPlatformPnlPage. The backend
 * handler is gated server-side; this client is a thin GET wrapper.
 */
import apiClient from './apiClient';
import type {
  PlatformDashboardResponse,
  PlatformPnlResponse,
  UserRosterResponse,
} from '../types/analytics';

const API_TYPE = 'api' as const;

class PlatformPnlService {
  /**
   * Fetch the platform-wide per-user PnL snapshot for a single trading date.
   *
   * @param date YYYY-MM-DD in IST (the date the analytics aggregator stamped
   *             rows under). The aggregator runs at 16:10 IST weekdays —
   *             requests for today before that time return an empty rows list.
   */
  async getPlatformPnl(date: string): Promise<PlatformPnlResponse> {
    const params = new URLSearchParams({ date });
    return apiClient.get<PlatformPnlResponse>(
      `/analytics/platform-pnl?${params.toString()}`,
      undefined,
      API_TYPE,
    );
  }

  /**
   * Fetch the platform-wide user roster — one row per user with lifetime
   * stats (sourced from stamped cumulatives on each user's most recent
   * analytics-daily row). No date selection; never-traded users included
   * with zeroed stats. SuperAdmin-only server-side.
   */
  async getUserRoster(): Promise<UserRosterResponse> {
    return apiClient.get<UserRosterResponse>(
      `/analytics/user-roster`,
      undefined,
      API_TYPE,
    );
  }

  /**
   * Fetch platform-wide daily snapshots for the SuperAdmin Dashboard tab.
   * Each snapshot is one PLATFORM sentinel row written by the analytics
   * aggregator at 16:10 IST. SuperAdmin-only server-side.
   */
  async getPlatformDashboard(start: string, end: string): Promise<PlatformDashboardResponse> {
    const params = new URLSearchParams({ start, end });
    return apiClient.get<PlatformDashboardResponse>(
      `/analytics/platform-dashboard?${params.toString()}`,
      undefined,
      API_TYPE,
    );
  }
}

const platformPnlService = new PlatformPnlService();
export default platformPnlService;
